// ChartPreset library for the Site Overview ChartCard (djin-cloud#67 Phase 5).
//
// MVP library — curated, per design §4.5 / §8 (no arbitrary metric × viz builder).
// All presets back onto `/v1/sites/:id/timeseries` with the existing metric
// names; no backend change required (site-scope metric names are produced by
// `computeSiteSummary` in event-hub-consumer.service.ts).
//
// Availability rules gate presets by site asset composition:
//   - `bess_soc`         → site has `battery_storage` in asset_types
//   - `fuel_consumption` → site has `generator`     in asset_types
//   - `voltage`          → site has `generator`     in asset_types
//                          (computeSiteSummary derives grid_voltage from
//                          generator phase voltages — meter-only sites would
//                          render a flat 0 line, so gate the preset)
//   - `power`, `reactive_power`, `power_frequency`, `load_pct`, `frequency`
//     are universal (no rule).
//
// `power` is the DEFAULT whenever localStorage is absent OR the stored id
// fails its availability rule at the current site. See `resolvePresetId`.
//
// `intervalDefault` is `1h` on EVERY preset — deliberately, not incidentally
// (issue #636). Two reasons, both load-bearing:
//
//   1. First paint. `/v1/sites/:id/timeseries` probes `site_kpis` first and
//      falls back to raw `telemetry` only when that returns zero rows. Every
//      site-scope metric here (`total_load`, `grid_frequency`, …) is written by
//      `computeSiteSummary` to `telemetry`, NOT `site_kpis` — so the probe is a
//      guaranteed miss and the real query always scans the raw hypertable,
//      filtered on `tags->>'asset_type'`. No rollup table is consulted. A 24h
//      window scanned ~24x the rows of a 1h one and left the card blank for
//      ~5s after page load.
//   2. Uniformity. ChartCard resets the interval to the newly-selected preset's
//      `intervalDefault` on every preset switch, so a mixed config would make
//      switching presets silently yank the window back to 24h — reads as a bug.
//
// Accepted trade-off: a site that stopped reporting >1h ago shows an empty
// chart where 24h would still show its last data. The 6H/24H/30D buttons are
// one click away, and a blank chart on a dead site beats a stale one.

import type { ChartPreset, ChartPresetId } from '../types/chart.types';
import type { SiteConfig } from '../types/project.types';

export const DEFAULT_CHART_PRESET_ID: ChartPresetId = 'power';

const PRESET_ORDER: ChartPresetId[] = [
  'power',
  'power_frequency',
  'reactive_power',
  'load_pct',
  'voltage',
  'frequency',
  'fuel_consumption',
  'bess_soc',
];

export const CHART_PRESETS: Readonly<Record<ChartPresetId, ChartPreset>> = {
  power: {
    id: 'power',
    label: 'Power',
    description: 'Total site load (kW).',
    // DB-canonical site-scope metric name (see note on load_pct.metric below).
    // Written by computeSiteSummary as the `total_load` site-summary row.
    metric: 'total_load',
    assetType: 'site',
    chartType: 'area',
    aggregation: 'avg',
    intervalDefault: '1h',
    intervalOptions: ['1h', '6h', '24h', '30d'],
    unit: 'kW',
  },
  load_pct: {
    id: 'load_pct',
    label: 'Load % over time',
    description: 'Site utilization as % of online generator capacity.',
    // NOTE: metric name is the DB-canonical (non-unit-suffixed) name written by
    // computeSiteSummary to `telemetry`. The `/v1/sites/:id/timeseries` route
    // queries on this exact name — the unit-suffixed `site_utilization_pct`
    // exists only on the live WebSocket `site_summary.*` payload, not in DB.
    metric: 'site_utilization',
    assetType: 'site',
    chartType: 'bar',
    aggregation: 'avg',
    intervalDefault: '1h',
    intervalOptions: ['1h', '6h', '24h', '30d'],
    yDomain: [0, 100],
    unit: '%',
  },
  power_frequency: {
    id: 'power_frequency',
    label: 'Power & Frequency',
    description: 'Total site load (kW) alongside grid frequency (Hz).',
    // DB-canonical names (see note on load_pct.metric above).
    metric: 'total_load,grid_frequency',
    assetType: 'site',
    // TODO(djin-cloud#67): upgrade to true dual-axis when AreaChart/LineChart
    // gain `rightYAxisKey` support. MVP renders multi-series line/area — one
    // shared Y-axis — per design §4.5 "degrade if dual-axis balloons scope".
    chartType: 'dual-axis',
    aggregation: 'avg',
    intervalDefault: '1h',
    intervalOptions: ['1h', '6h', '24h', '30d'],
    unit: 'kW / Hz',
  },
  reactive_power: {
    id: 'reactive_power',
    label: 'Reactive Power',
    description: 'Total site reactive power (kVAR).',
    // DB-canonical site-scope metric name (see note on load_pct.metric above).
    // Written by computeSiteSummary as the `total_reactive_power` site-summary
    // row, aggregated from per-generator reactive_power_kvar rows.
    metric: 'total_reactive_power',
    assetType: 'site',
    chartType: 'line',
    aggregation: 'avg',
    intervalDefault: '1h',
    intervalOptions: ['1h', '6h', '24h', '30d'],
    unit: 'kVAR',
  },
  voltage: {
    id: 'voltage',
    label: 'Voltage',
    description: 'Average generator output voltage.',
    // DB-canonical site-scope metric name (see note on load_pct.metric above).
    // Written by computeSiteSummary (event-hub-consumer.service.ts:1166) from
    // generator voltage_phase_a/b/c + avg_line_voltage rows; unit on disk is V.
    // Gated by generator presence because that's the only producer today —
    // BESS-only sites would render a flat 0 line.
    metric: 'grid_voltage',
    assetType: 'site',
    chartType: 'line',
    aggregation: 'avg',
    intervalDefault: '1h',
    intervalOptions: ['1h', '6h', '24h', '30d'],
    unit: 'V',
    availabilityRule: (s) => (s.asset_types ?? []).includes('generator'),
  },
  frequency: {
    id: 'frequency',
    label: 'Frequency',
    description: 'Grid frequency at the site meter.',
    // DB-canonical site-scope metric name (see note on load_pct.metric above).
    // Produced by computeSiteSummary.
    metric: 'grid_frequency',
    assetType: 'site',
    chartType: 'line',
    aggregation: 'avg',
    intervalDefault: '1h',
    intervalOptions: ['1h', '6h', '24h', '30d'],
    unit: 'Hz',
  },
  fuel_consumption: {
    id: 'fuel_consumption',
    label: 'Fuel consumption',
    description: 'Site total fuel consumption (MMBtu/h).',
    // DB-canonical name (see note on load_pct.metric above).
    metric: 'fuel_consumption',
    assetType: 'site',
    chartType: 'line',
    aggregation: 'avg',
    intervalDefault: '1h',
    intervalOptions: ['1h', '6h', '24h', '30d'],
    unit: 'MMBtu/h',
    availabilityRule: (s) => (s.asset_types ?? []).includes('generator'),
  },
  bess_soc: {
    id: 'bess_soc',
    label: 'Battery SOC',
    description: 'Battery state of charge (%).',
    metric: 'state_of_charge',
    assetType: 'battery_storage',
    chartType: 'line',
    aggregation: 'avg',
    intervalDefault: '1h',
    intervalOptions: ['1h', '6h', '24h', '30d'],
    yDomain: [0, 100],
    unit: '%',
    availabilityRule: (s) => (s.asset_types ?? []).includes('battery_storage'),
  },
};

/**
 * Returns the presets available at this site, preserving the canonical order.
 * A preset is available when its `availabilityRule` is absent or returns true.
 */
export function getAvailablePresets(site: SiteConfig): ChartPreset[] {
  return PRESET_ORDER.map((id) => CHART_PRESETS[id]).filter(
    (p) => !p.availabilityRule || p.availabilityRule(site),
  );
}

/**
 * Resolve a localStorage-stored id against the current site.
 *
 *   - Valid `ChartPresetId` AND availability rule passes → return it.
 *   - Anything else (null, unknown string, gated-off id) → `DEFAULT_CHART_PRESET_ID`.
 *
 * The default (`power`) has no availability rule, so this function is
 * total — it always returns an id that is renderable at the given site.
 */
export function resolvePresetId(
  stored: string | null,
  site: SiteConfig,
): ChartPresetId {
  if (stored && isChartPresetId(stored)) {
    const preset = CHART_PRESETS[stored];
    if (!preset.availabilityRule || preset.availabilityRule(site)) {
      return stored;
    }
  }
  return DEFAULT_CHART_PRESET_ID;
}

function isChartPresetId(s: string): s is ChartPresetId {
  return s in CHART_PRESETS;
}
